import React from 'react'
import clsx from 'clsx'
import { CalendarDays, Filter } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  dashboardPresetLabel,
  DASHBOARD_SELECTABLE_PRESETS,
  resolvePeriod,
  type DashboardPeriod,
  type DashboardPeriodPreset,
} from '@/lib/dashboardPeriod'
import {
  accountDayKey,
  endOfAccountDay,
  startOfAccountDayFromKey,
} from '@/lib/accountTimezone'

const ALL_PIPELINES = '__all__'

/** The picker shows and reads calendar days of the account timezone, matching the buckets. */
function inputDate(date: Date) {
  return accountDayKey(date)
}

interface DashboardFiltersProps {
  period: DashboardPeriod
  onPeriodChange: (period: DashboardPeriod) => void
  pipelines: { id: string; name: string }[]
  pipelineId: string | null
  onPipelineChange: (pipelineId: string | null) => void
  pipelinesLoading?: boolean
  comparisonLabel: string
}

const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  period,
  onPeriodChange,
  pipelines,
  pipelineId,
  onPipelineChange,
  pipelinesLoading = false,
  comparisonLabel,
}) => {
  const { t } = useTranslation('reports')

  const handlePreset = (preset: DashboardPeriodPreset) => {
    if (preset === 'custom') {
      onPeriodChange({ ...period, preset: 'custom' })
      return
    }
    onPeriodChange(resolvePeriod(preset))
  }

  return (
    <div className="flex flex-col gap-3 rounded-[20px] border border-border bg-card p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 pr-1 text-muted-foreground">
          <CalendarDays className="h-4 w-4" />
          <span className="text-[12px] font-medium">{t('dashboard.filterPeriod')}</span>
        </div>

        <div className="flex flex-wrap items-center gap-1 rounded-full bg-card-muted p-1">
          {DASHBOARD_SELECTABLE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => handlePreset(preset)}
              className={clsx(
                'rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors',
                period.preset === preset
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {dashboardPresetLabel(preset)}
            </button>
          ))}
          <button
            type="button"
            onClick={() => handlePreset('custom')}
            className={clsx(
              'rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors',
              period.preset === 'custom'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {dashboardPresetLabel('custom')}
          </button>
        </div>

        {period.preset === 'custom' && (
          <div className="flex items-end gap-2">
            <Input
              type="date"
              aria-label={t('dashboard.filterDateFrom')}
              value={inputDate(period.from)}
              max={inputDate(period.to)}
              onChange={(event) => {
                const from = startOfAccountDayFromKey(event.target.value)
                if (!from) return
                onPeriodChange({ preset: 'custom', from, to: period.to })
              }}
              className="h-9 w-[144px]"
            />
            <span className="pb-2 text-[12px] text-muted-foreground">{t('dashboard.filterUntil')}</span>
            <Input
              type="date"
              aria-label={t('dashboard.filterDateTo')}
              value={inputDate(period.to)}
              min={inputDate(period.from)}
              onChange={(event) => {
                const day = startOfAccountDayFromKey(event.target.value)
                if (!day) return
                onPeriodChange({ preset: 'custom', from: period.from, to: endOfAccountDay(day) })
              }}
              className="h-9 w-[144px]"
            />
          </div>
        )}

        <span className="text-[11px] text-muted-foreground">{comparisonLabel}</span>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span className="text-[12px] font-medium">{t('dashboard.filterFunnel')}</span>
        </div>
        <Select
          value={pipelineId ?? ALL_PIPELINES}
          onValueChange={(value) => onPipelineChange(value === ALL_PIPELINES ? null : value)}
          disabled={pipelinesLoading}
        >
          <SelectTrigger className="h-9 w-full min-w-[200px] lg:w-[240px]">
            <SelectValue placeholder={pipelinesLoading ? t('common:shared.loading') : t('dashboard.filterAllFunnels')} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={ALL_PIPELINES}>{t('dashboard.filterAllFunnels')}</SelectItem>
              {pipelines.map((pipeline) => (
                <SelectItem key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

export default DashboardFilters
